/**
 * Pure helpers for managing the map of live Transform instances (each owns a
 * Web Worker + blob URL). Kept dependency-free so they can be unit tested
 * without the framework barrel (see repo testing note).
 *
 * EID-57: every replacement or removal of a registry entry must terminate the
 * outgoing Transform, otherwise its worker and blob URL leak.
 */

export interface Terminable {
  terminate(): void;
}

/**
 * Return a new registry with `next` stored under `id`, terminating any
 * existing entry for that id first.
 */
export const replaceTransform = <T extends Terminable>(
  prev: Record<string, T>,
  id: string,
  next: T,
): Record<string, T> => {
  const existing = prev[id];
  if (existing && existing !== next) {
    existing.terminate();
  }
  return { ...prev, [id]: next };
};

/**
 * Return a new registry without `id`, terminating the outgoing Transform
 * (EID-61: transforms are now removed per-id when their spec entry is
 * removed or changed, instead of terminating the whole registry).
 */
export const removeTransform = <T extends Terminable>(
  prev: Record<string, T>,
  id: string,
): Record<string, T> => {
  const existing = prev[id];
  if (!existing) return prev;
  existing.terminate();
  const next = { ...prev };
  delete next[id];
  return next;
};

/** Terminate every Transform in the registry (full reset / unmount). */
export const terminateAll = (registry: Record<string, Terminable>): void => {
  for (const id of Object.keys(registry)) {
    registry[id]?.terminate();
  }
};
